import { Helmet } from "react-helmet-async";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  CheckCircle2, 
  Clock, 
  Users, 
  ListTodo, 
  Bell, 
  Sparkles,
  ArrowRight,
  HelpCircle,
  ExternalLink
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { RelatedAlternatives } from "@/components/RelatedAlternatives";

const Astrid = () => {
  const faqData = [
    {
      question: "What happened to Astrid?",
      answer: "Astrid was acquired by Yahoo in May 2013 and shut down on August 5, 2013—just 90 days after acquisition. Yahoo offered no detailed explanation for discontinuing the beloved task app."
    },
    {
      question: "Is there a free alternative to Astrid?",
      answer: "Yes! CSW offers free task management features along with study timers, reminders, and collaborative study rooms. It combines productivity tools that Astrid users loved with focused study sessions."
    },
    {
      question: "What made Astrid special?",
      answer: "Astrid was known for its friendly octopus mascot, social task sharing via @mentions, and its playful approach to productivity. It had 4 million users before Yahoo shut it down."
    },
    {
      question: "How does CSW compare to Astrid?",
      answer: "CSW offers task management and collaboration features similar to Astrid, plus unique study-focused tools like Pomodoro timers, virtual study rooms, and productivity tracking."
    },
    {
      question: "Does CSW work on all devices like Astrid did?",
      answer: "Yes! CSW is a web-based platform that works seamlessly on any device with a browser—Windows, Mac, iOS, Android, and more."
    },
    {
      question: "Can I share tasks with others on CSW like Astrid allowed?",
      answer: "Absolutely! CSW offers collaborative study rooms where you can work alongside others, share goals, and stay accountable—taking Astrid's social task sharing concept further."
    }
  ];

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Astrid Alternative - Free Task Management & Study Tools",
    "description": "Looking for an Astrid alternative? Astrid was shut down by Yahoo in 2013 but CSW offers free Task Management, Study Timer, and collaborative productivity tools.",
    "url": "https://chillstudy.work/astrid/",
    "inLanguage": "en",
    "isPartOf": {
      "@type": "WebSite",
      "name": "Chill Study Work",
      "url": "https://chillstudy.work"
    },
    "about": {
      "@type": "SoftwareApplication",
      "name": "Astrid",
      "applicationCategory": "ProductivityApplication"
    },
    "mentions": [
      {
        "@type": "SoftwareApplication",
        "name": "Tasks.org",
        "url": "https://tasks.org/"
      },
      {
        "@type": "SoftwareApplication",
        "name": "Any.do",
        "url": "https://www.any.do/"
      }
    ],
    "keywords": "Astrid alternative, Astrid replacement, task management app, Yahoo Astrid, to-do list app, productivity tools"
  };

  const softwareSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Chill Study Work - Astrid Alternative",
    "applicationCategory": "ProductivityApplication",
    "operatingSystem": "Web Browser",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "ratingCount": "1200",
      "bestRating": "5",
      "worstRating": "1"
    },
    "featureList": [
      "Task Management with Lists",
      "Due Dates and Reminders",
      "Pomodoro Study Timer",
      "Collaborative Study Rooms",
      "Progress Tracking",
      "Cross-Platform Sync",
      "Social Accountability",
      "Focus Mode"
    ],
    "publisher": {
      "@type": "Organization",
      "name": "Chill Study Work",
      "url": "https://chillstudy.work"
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Astrid Alternative - Free Task Management & Study Tools | CSW</title>
        <meta name="description" content="Looking for an Astrid alternative? Astrid was shut down by Yahoo in 2013 but CSW offers free Task Management, Study Timer, and collaborative productivity tools." />
        <meta name="keywords" content="Astrid alternative, Astrid replacement, task management app, Yahoo Astrid, to-do list app, productivity tools, study timer" />
        <link rel="canonical" href="https://chillstudy.work/astrid/" />
        <meta property="og:title" content="Astrid Alternative - Free Task Management & Study Tools" />
        <meta property="og:description" content="Astrid was shut down by Yahoo in 2013. Discover CSW's free task management, study timer, and productivity tools." />
        <meta property="og:url" content="https://chillstudy.work/astrid/" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(webPageSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(softwareSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <Header />

      <main className="pt-24 pb-16">
        {/* Hero Section */}
        <section className="container mx-auto px-4 mb-16">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-destructive/10 text-destructive px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Clock className="w-4 h-4" />
              Astrid shut down on August 5, 2013
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
              The Best Astrid Alternative for Social Task Management
            </h1>
            <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
              Miss Astrid's friendly octopus and social task sharing? CSW combines collaborative task management with 
              <Link to="/study-with-me/" className="text-primary hover:underline"> focused study sessions</Link>, 
              accountability partners, and productivity tracking—all for free.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="text-lg px-8">
                <a href="https://app.chillstudy.work" target="_blank" rel="noopener noreferrer">
                  Try CSW Free
                  <ArrowRight className="w-5 h-5 ml-2" />
                </a>
              </Button>
              <Button asChild variant="outline" size="lg" className="text-lg px-8">
                <Link to="/astrid/history/">
                  Read Astrid's Story
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Shutdown Notice */}
        <section className="container mx-auto px-4 mb-16">
          <div className="max-w-4xl mx-auto bg-card rounded-2xl p-8 border border-border/50 shadow-lg">
            <h2 className="text-2xl font-bold mb-4">What Happened to Astrid?</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              <a href="https://en.wikipedia.org/wiki/Astrid_(application)" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Astrid</a> was 
              a beloved task management app created in 2008 by Tim Su and Jon Paris in San Francisco. Known for its friendly 
              octopus mascot and social features, Astrid grew to over 4 million users who loved its playful approach to productivity.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-4">
              In May 2013, <a href="https://techcrunch.com/2013/07/06/astrid-goes-dark-august-5-goodnight-sweet-squid/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Yahoo acquired Astrid</a> as 
              part of CEO Marissa Mayer's startup acquisition spree. Just 90 days later, Yahoo shut down the service entirely on August 5, 2013.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              If you're looking for an alternative that combines task management with collaborative features, 
              CSW offers <Link to="/task-management/" className="text-primary hover:underline">task management</Link>,{" "}
              <Link to="/study-timer/" className="text-primary hover:underline">study timers</Link>,{" "}
              <Link to="/reminders/" className="text-primary hover:underline">reminders</Link>, and{" "}
              <Link to="/group-study/" className="text-primary hover:underline">collaborative study rooms</Link>.
            </p>
          </div>
        </section>

        {/* Features Comparison */}
        <section className="container mx-auto px-4 mb-16">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">
              Everything You Loved About Astrid, Plus More
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-card rounded-xl p-6 border border-border/50">
                <div className="p-3 bg-primary/10 rounded-lg w-fit mb-4">
                  <ListTodo className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Task Management</h3>
                <p className="text-muted-foreground">
                  Create lists, add tasks with due dates, and organize your work just like Astrid. 
                  <Link to="/task-management/" className="text-primary hover:underline ml-1">Learn more</Link>
                </p>
              </div>

              <div className="bg-card rounded-xl p-6 border border-border/50">
                <div className="p-3 bg-accent/10 rounded-lg w-fit mb-4">
                  <Users className="w-6 h-6 text-accent" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Social Collaboration</h3>
                <p className="text-muted-foreground">
                  Share tasks and work together in real-time study rooms—like Astrid's @mentions, but live.
                  <Link to="/study-with-friends/" className="text-primary hover:underline ml-1">Learn more</Link>
                </p>
              </div>

              <div className="bg-card rounded-xl p-6 border border-border/50">
                <div className="p-3 bg-green-500/10 rounded-lg w-fit mb-4">
                  <Clock className="w-6 h-6 text-green-500" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Pomodoro Timer</h3>
                <p className="text-muted-foreground">
                  Built-in study timer with Pomodoro technique—something Astrid never had.
                  <Link to="/study-timer/" className="text-primary hover:underline ml-1">Learn more</Link>
                </p>
              </div>

              <div className="bg-card rounded-xl p-6 border border-border/50">
                <div className="p-3 bg-purple-500/10 rounded-lg w-fit mb-4">
                  <Bell className="w-6 h-6 text-purple-500" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Smart Reminders</h3>
                <p className="text-muted-foreground">
                  Never miss a deadline with customizable reminders and notifications.
                  <Link to="/reminders/" className="text-primary hover:underline ml-1">Learn more</Link>
                </p>
              </div>

              <div className="bg-card rounded-xl p-6 border border-border/50">
                <div className="p-3 bg-orange-500/10 rounded-lg w-fit mb-4">
                  <Sparkles className="w-6 h-6 text-orange-500" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Ambient Sounds</h3>
                <p className="text-muted-foreground">
                  Focus better with lo-fi beats, rain sounds, and café ambiance.
                  <Link to="/studystream/" className="text-primary hover:underline ml-1">Learn more</Link>
                </p>
              </div>

              <div className="bg-card rounded-xl p-6 border border-border/50">
                <div className="p-3 bg-blue-500/10 rounded-lg w-fit mb-4">
                  <CheckCircle2 className="w-6 h-6 text-blue-500" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Progress Tracking</h3>
                <p className="text-muted-foreground">
                  Track your study hours and completed tasks to build productive habits.
                  <Link to="/productivity-tool/" className="text-primary hover:underline ml-1">Learn more</Link>
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Other Alternatives */}
        <section className="container mx-auto px-4 mb-16">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold mb-6">Other Astrid Alternatives</h2>
            <p className="text-muted-foreground mb-6">
              While CSW is designed for students and focused productivity, here are other popular task management apps 
              that former Astrid users have migrated to:
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              <a href="https://tasks.org/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-4 bg-card rounded-lg border border-border/50 hover:border-primary/50 transition-colors">
                <ExternalLink className="w-5 h-5 text-primary" />
                <div>
                  <p className="font-semibold">Tasks.org</p>
                  <p className="text-sm text-muted-foreground">Open-source fork of Astrid's code</p>
                </div>
              </a>
              <a href="https://www.any.do/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-4 bg-card rounded-lg border border-border/50 hover:border-primary/50 transition-colors">
                <ExternalLink className="w-5 h-5 text-primary" />
                <div>
                  <p className="font-semibold">Any.do</p>
                  <p className="text-sm text-muted-foreground">Sleek task and calendar app</p>
                </div>
              </a>
              <a href="https://todoist.com/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-4 bg-card rounded-lg border border-border/50 hover:border-primary/50 transition-colors">
                <ExternalLink className="w-5 h-5 text-primary" />
                <div>
                  <p className="font-semibold">Todoist</p>
                  <p className="text-sm text-muted-foreground">Powerful cross-platform task manager</p>
                </div>
              </a>
              <a href="https://ticktick.com/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-4 bg-card rounded-lg border border-border/50 hover:border-primary/50 transition-colors">
                <ExternalLink className="w-5 h-5 text-primary" />
                <div>
                  <p className="font-semibold">TickTick</p>
                  <p className="text-sm text-muted-foreground">Tasks + calendar + habit tracking</p>
                </div>
              </a>
            </div>
          </div>
        </section>

        {/* Blog Links */}
        <section className="container mx-auto px-4 mb-16">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold mb-6">Productivity Resources</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Link to="/blog/how-to-stop-procrastination/" className="p-4 bg-card rounded-lg border border-border/50 hover:border-primary/50 transition-colors">
                <p className="font-semibold mb-1">How to Stop Procrastination</p>
                <p className="text-sm text-muted-foreground">Science-backed strategies to get things done</p>
              </Link>
              <Link to="/blog/apps-boost-organization-college/" className="p-4 bg-card rounded-lg border border-border/50 hover:border-primary/50 transition-colors">
                <p className="font-semibold mb-1">Best Organization Apps for College</p>
                <p className="text-sm text-muted-foreground">Top apps to stay organized in school</p>
              </Link>
              <Link to="/blog/top-10-digital-tools-for-academic-success/" className="p-4 bg-card rounded-lg border border-border/50 hover:border-primary/50 transition-colors">
                <p className="font-semibold mb-1">Top 10 Digital Tools for Academic Success</p>
                <p className="text-sm text-muted-foreground">Essential tools every student needs</p>
              </Link>
              <Link to="/blog/science-behind-study-with-me/" className="p-4 bg-card rounded-lg border border-border/50 hover:border-primary/50 transition-colors">
                <p className="font-semibold mb-1">The Science Behind Study With Me</p>
                <p className="text-sm text-muted-foreground">Why studying together works</p>
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="container mx-auto px-4 mb-16">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <div className="p-2 bg-primary/10 rounded-lg">
                <HelpCircle className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-2xl font-bold">Frequently Asked Questions</h2>
            </div>
            <Accordion type="single" collapsible className="w-full">
              {faqData.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`}>
                  <AccordionTrigger className="text-left">{faq.question}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">{faq.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTA */}
        <section className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-gradient-to-br from-primary/10 via-accent/10 to-primary/10 rounded-2xl p-8 text-center border border-primary/20">
            <h2 className="text-2xl font-bold mb-4">Ready to Be Productive Again?</h2>
            <p className="text-muted-foreground mb-6">
              Join thousands of students and professionals who use CSW to stay focused and get things done together.
            </p>
            <Button asChild size="lg" className="text-lg px-8">
              <a href="https://app.chillstudy.work" target="_blank" rel="noopener noreferrer">
                Try CSW Free
                <ArrowRight className="w-5 h-5 ml-2" />
              </a>
            </Button>
          </div>
        </section>
      </main>

      <RelatedAlternatives currentPlatform="Astrid" />

      <Footer />
    </div>
  );
};

export default Astrid;
